import { useEffect, useState } from "react";
import { Link2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

/**
 * ReferralGateBanner
 *
 * Shows on the Dashboard for members who signed up via a referral link
 * but haven't yet linked their Discord — which means their referrer's
 * 3-referral counter is still stuck. Two goals:
 *
 *   1. Tell the referee they're literally one 15-second step from
 *      closing their friend's referral (guilt-nudge, but honest).
 *   2. Give them a single "Link Discord" button that fires the
 *      OAuth flow.
 *
 * Auto-hides once ``user.discord_linked`` flips to true, and can be
 * dismissed via the × button (persisted 24h in ``localStorage``).
 */
const DISMISS_KEY = "pms_referral_gate_banner_dismissed_at";
const DISMISS_HOURS = 24;

export default function ReferralGateBanner() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [referrerName, setReferrerName] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      const iso = localStorage.getItem(DISMISS_KEY);
      if (iso) {
        const ageH = (Date.now() - new Date(iso).getTime()) / 3600_000;
        if (ageH < DISMISS_HOURS) setDismissed(true);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    // Only look up the referrer's name if the banner would actually show.
    if (!user || !user.referred_by_code || user.discord_linked) return;
    if (user.tier !== "basic" && user.tier !== "premium") return;
    api.get(`/referrals/lookup/${encodeURIComponent(user.referred_by_code)}`)
      .then((r) => setReferrerName(r.data?.referrer_name || null))
      .catch(() => {});
  }, [user]);

  if (!user) return null;
  if (dismissed) return null;
  if (user.discord_linked) return null;
  if (!user.referred_by_code) return null;
  // Only nudge paid members — free-tier users don't count for the referrer
  // anyway, so telling them to link Discord is misleading.
  if (user.tier !== "basic" && user.tier !== "premium") return null;

  const linkDiscord = () => {
    const API = process.env.REACT_APP_BACKEND_URL || "";
    window.location.href = `${API}/api/discord/oauth/start`;
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, new Date().toISOString()); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      data-testid="referral-gate-banner"
      className="relative rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-white p-5 pr-12"
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label={t("referralGateUi.dismissAriaLabel")}
        data-testid="referral-gate-banner-dismiss"
        className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-colors"
      >
        <X size={14} />
      </button>
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-10 h-10 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center">
          <Link2 size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-indigo-800">
            {t("referralGateUi.oneStepLabel")}
          </div>
          <div className="mt-1 font-display font-bold text-slate-900 text-[15px] leading-snug">
            {referrerName
              ? t("referralGateUi.headlineWithName", { name: referrerName })
              : t("referralGateUi.headlineGeneric")}
          </div>
          <div className="mt-1 text-sm text-slate-600 leading-relaxed">
            {t("referralGateUi.description")}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={linkDiscord}
              data-testid="referral-gate-banner-link"
              className="inline-flex items-center gap-2 rounded-full bg-[#5865F2] hover:bg-[#4752C4] text-white text-sm font-semibold px-4 py-2 transition-colors"
            >
              {t("referralGateUi.linkDiscordButton")}
            </button>
            <button
              type="button"
              onClick={dismiss}
              className="text-xs text-slate-500 hover:text-slate-900 underline underline-offset-2"
              data-testid="referral-gate-banner-later"
            >
              {t("referralGateUi.laterButton")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
